import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { addIElementsToStore } from "@/store/ielements-store-utilities";
import { AppDispatch } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { GUID, assert } from "@faro-lotv/foundation";
import {
  IElement,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";
import {
  addAreaDataSets,
  fetchProjectIElements,
  selectAreaCaptureTreeDataSetIds,
} from "@faro-lotv/project-source";
import {
  DataSetAreaInfo,
  ProjectApi,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { isEqual } from "es-toolkit";
import {
  MutableRefObject,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import {
  selectAllSubtreesLoaded,
  useIDsToFetch,
} from "./project-loading-selectors";

/** After how many seconds to consider a sub-tree stale and in needs to be re-fetched */
const STALE_TIME = 300;

/** Collection of requests to get IElements */
export type IElementFetchRequests = Array<Promise<IElement[]>>;

/** String that represent a specific ProjectApi fetch request encoding the subtree parameters */
type SubTreeRequestHash = string;

/** Information about the fetching of a sub-tree */
type TreeFetchData = {
  /** True if this fetch is in progress */
  isInProgress: boolean;

  /** Timeout handle for the setTimeout used to schedule the abort of this fetch  */
  abortTimeout?: number;

  /** Date of when this fetch was successfully executed */
  lastSuccessfulFetch?: Date;
};

/** Project context data provided by the ProjectProvider */
type ProjectLoadingContext = {
  /** The authenticated client to fetch data from the ProjectAPI */
  client: ProjectApi;

  /** Map to cache of all the individual element fetches */
  elementFetches: MutableRefObject<Map<SubTreeRequestHash, TreeFetchData>>;

  /** Map to cache of all the individual parent fetches */
  parentFetches: MutableRefObject<Map<SubTreeRequestHash, TreeFetchData>>;

  /** Map to cache of all the sub-tree fetches */
  subTreeFetches: MutableRefObject<Map<SubTreeRequestHash, TreeFetchData>>;
};

export const ProjectLoadingContext = createContext<
  ProjectLoadingContext | undefined
>(undefined);

/** @returns a context that offers functionality to incrementally load areas and sub-trees of a project */
export function ProjectLoadingContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const { projectApiClient: client } = useApiClientContext();

  const elementFetches = useRef(new Map<SubTreeRequestHash, TreeFetchData>());

  const parentFetches = useRef(new Map<SubTreeRequestHash, TreeFetchData>());

  const subTreeFetches = useRef(new Map<SubTreeRequestHash, TreeFetchData>());

  // When the client changes we need to clear the cache for all the fetches as
  // they're client specific
  useEffect(() => {
    elementFetches.current.clear();
    parentFetches.current.clear();
    subTreeFetches.current.clear();
  }, [client]);

  const value = useMemo<ProjectLoadingContext>(
    () => ({
      client,
      elementFetches,
      parentFetches,
      subTreeFetches,
    }),
    [client],
  );

  return (
    <ProjectLoadingContext value={value}>{children}</ProjectLoadingContext>
  );
}

/**
 * @returns the current bound ProjectProvider context
 */
function useProjectLoadingContext(): ProjectLoadingContext {
  const ctx = useContext(ProjectLoadingContext);

  assert(
    ctx,
    "useProjectLoadingContext can be used only inside a ProjectProvider",
  );

  return ctx;
}

/**
 * @returns The current ProjectApi client instance used to fetch project data or undefined in a demo project
 * @deprecated Use `useApiClientContext` instead.
 */
export function useCurrentProjectApiClient(): ProjectApi {
  return useProjectLoadingContext().client;
}

/**
 * Fetch a project api sub-tree from the backend
 *
 * @param id of the node to download the subtrees for
 * @returns true if the loading is in progress
 */
function useProjectSubTree(id: GUID | undefined): boolean {
  return useProjectSubTrees(id ? [id] : undefined);
}

type RunVolumeQueryRet = {
  isLoading: boolean;
  dataSetIds: GUID[];
};

function useRunVolumeQueryForArea(areaId: GUID | undefined): RunVolumeQueryRet {
  const dataSetIds = useAppSelector((state) =>
    selectAreaCaptureTreeDataSetIds(state, areaId),
  );

  const isAreaLoading = useProjectSubTree(areaId);
  // If the dataSetIds list is undefined, it means that we still need to run the volume query for the current area
  // If it is defined, we already ran the volume query and we do not issue it again.
  const isVolumeQueryRunning = useLoadAreaDataSets(
    dataSetIds === undefined ? areaId : undefined,
  );

  return {
    isLoading: isAreaLoading || isVolumeQueryRunning,
    dataSetIds: dataSetIds ?? [],
  };
}

/**
 * @param areaId of the area to load the BI sub-tree and all the datasets
 * @returns true if the area or some datasets inside the area are still loading
 */
export function useLoadProjectArea(areaId: GUID | undefined): boolean {
  const { isLoading, dataSetIds } = useRunVolumeQueryForArea(areaId);
  const areDataSetLoading = useProjectSubTrees(isLoading ? [] : dataSetIds);
  return isLoading || areDataSetLoading;
}

/**
 * Load from the backend all the datasets that are contained in an area volume
 *
 * @param areaId to query the volume for datasets. If undefined, the contents for the entire project are loaded.
 * @returns true while the volume query is running
 */
function useLoadAreaDataSets(areaId: GUID | undefined): boolean {
  // Need additional null state to distinguish from loading the "entire project".
  const [lastLoadedArea, setLastLoadedArea] = useState<GUID | undefined | null>(
    null,
  );
  const projectApi = useCurrentProjectApiClient();
  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    const ac = new AbortController();
    if (!areaId) {
      return;
    }

    projectApi
      .queryAreaVolume(areaId, ac.signal)
      .then((dataSets: DataSetAreaInfo[]) => {
        dispatch(addAreaDataSets({ areaId, dataSets }));
      })
      .finally(() => {
        if (!ac.signal.aborted) {
          setLastLoadedArea(areaId);
        }
      });

    return () => {
      ac.abort();
    };
  }, [areaId, dispatch, projectApi]);

  // Explicit loading state is too slow. The hook needs to return true immediately when the area changes.
  return !!areaId && lastLoadedArea !== areaId;
}

/**
 * Fetch a set of project api sub-trees from the backend
 *
 * @param ids of the nodes to download the subtrees for
 * @returns true if the loading is in progress
 */
function useProjectSubTrees(ids: GUID[] | undefined): boolean {
  const dispatch = useAppDispatch();
  const { handleErrorWithPage } = useErrorHandlers();
  const ctx = useProjectLoadingContext();
  // On the first render, the fetch has not started yet, but it needs to be indicated that the loading is in progress
  const [hasStarted, setHasStarted] = useState(false);
  const [fetchFinished, setFetchFinished] = useState(false);

  const idsToFetch = useIDsToFetch(ids);

  useLayoutEffect(() => {
    // Skip the fetch if there are no ids
    if (!idsToFetch.length) {
      return;
    }
    const now = new Date();

    // Compute the hash for this specific request
    const fetchId = idsToFetch.join("-");

    const { canSkip, lastValidFetch } = checkFetchStatus(fetchId, ctx, now);
    if (canSkip) {
      // If the hook is called from different places, the subtrees
      // could have been already loaded. We should set is as started anyway
      // so that the returned flag is false (not loading)
      setHasStarted(true);
      return;
    }

    // Create an object to store information on the new fetch
    const fetchData: TreeFetchData = {
      isInProgress: true,
      abortTimeout: undefined,
      lastSuccessfulFetch: undefined,
    };
    ctx.subTreeFetches.current.set(fetchId, fetchData);

    // Here we need to start a new fetch
    const abortController = new AbortController();

    dispatch(
      fetchProjectIElements({
        fetcher: () =>
          ctx.client.getAllIElements({
            signal: abortController.signal,
            ancestorIds: idsToFetch,
            changedAfter: lastValidFetch,
          }),
        loadingIds: idsToFetch,
      }),
    )
      .then((elements) => {
        // If fetch is successful store fetch time in the context cache
        fetchData.lastSuccessfulFetch = now;
        return elements;
      })
      .finally(() => {
        fetchData.isInProgress = false;
      })
      .catch((error: unknown) => {
        handleErrorWithPage(error);
        setFetchFinished(true);
      });

    setHasStarted(true);

    return () => {
      // When unmount schedule abort after a second so if we re-fetch this same request soon
      // we just keep the previous fetch alive (thanks React double mounts)
      fetchData.abortTimeout = window.setTimeout(() => {
        abortController.abort();
      }, 1000);
    };
  }, [ctx, dispatch, idsToFetch, handleErrorWithPage]);

  // We signal that the fetch is finished only when the requested subtrees are safely in the store
  // In this way, subsequent fetch functions can determine to not fetch elements already stored
  const isAllLoaded = useAppSelector(
    selectAllSubtreesLoaded(idsToFetch),
    isEqual,
  );

  useEffect(() => {
    setFetchFinished(isAllLoaded);
  }, [isAllLoaded]);

  // If the passed ids are empty we consider the loading finished
  return !!idsToFetch.length && (!hasStarted || !fetchFinished);
}

/**
 * Check if a new fetch can be skipped
 *
 * @param hash The hash of the new subTree fetch
 * @param ctx The project fetching context
 * @param now The current timestamp
 * @returns A boolean indicating if we can skip this fetch and the last valid fetch for this query if it exists
 */
function checkFetchStatus(
  hash: SubTreeRequestHash,
  ctx: ProjectLoadingContext,
  now: Date,
): { canSkip: boolean; lastValidFetch?: Date } {
  // If the data was already fetched and is not stale skip this fetch
  const fetchData = ctx.subTreeFetches.current.get(hash);
  if (!fetchData) {
    return { canSkip: false };
  }

  const { lastSuccessfulFetch } = fetchData;
  if (
    lastSuccessfulFetch &&
    now.getSeconds() - lastSuccessfulFetch.getSeconds() < STALE_TIME
  ) {
    return { canSkip: true, lastValidFetch: lastSuccessfulFetch };
  }

  // If the same fetch is already in progress skip this fetch
  if (fetchData.isInProgress) {
    // If the already in flight fetched was scheduled to abort
    // stop the timeout and keep it alive
    if (fetchData.abortTimeout) {
      clearTimeout(fetchData.abortTimeout);
    }
    return { canSkip: true, lastValidFetch: lastSuccessfulFetch };
  }

  return { canSkip: false, lastValidFetch: lastSuccessfulFetch };
}

type LoadedArea = {
  /** The list of elements in the area subtree */
  elements: IElement[];
  /** The list of datasets in the area */
  datasetInfo: DataSetAreaInfo[];
};

/**
 * @returns All the necessary info to fully load an area
 * @param areaId The id of the area to load
 * @param client The ProjectAPI client
 * @param controller The controller to abort the fetch
 */
async function fetchAreaData(
  areaId: GUID,
  client: ProjectApi,
  controller: AbortController = new AbortController(),
): Promise<LoadedArea> {
  const [elements, datasetInfo] = await Promise.all([
    // Fetch the area subtree
    client.getAllIElements({
      signal: controller.signal,
      ancestorIds: [areaId],
    }),
    // Fetch the area datasets
    client.queryAreaVolume(areaId, controller.signal),
  ]);

  // Fetch the subtree for datasets in the cluster tree
  const datasets = await Promise.all(
    datasetInfo
      .filter(
        (e) =>
          e.elementType === IElementType.section &&
          e.elementTypeHint === IElementTypeHint.dataSetCapture,
      )
      .map((info) =>
        client.getAllIElements({
          signal: controller.signal,
          ancestorIds: [info.elementId],
        }),
      ),
  );

  return { elements: [...elements, ...datasets.flat()], datasetInfo };
}

/**
 * Load the data related to a specific area and upload the store
 *
 * @param areaId The id of the area to load
 * @param client The instance of the ProjectAPI client
 * @param dispatch The callback to dispatch the updates the store
 * @param controller An optional abort controller to stop the loading
 */
export async function loadArea(
  areaId: GUID,
  client: ProjectApi,
  dispatch: AppDispatch,
  controller = new AbortController(),
): Promise<void> {
  const { elements, datasetInfo } = await fetchAreaData(
    areaId,
    client,
    controller,
  );
  addIElementsToStore(elements, dispatch);
  dispatch(
    addAreaDataSets({
      areaId,
      dataSets: datasetInfo,
    }),
  );
}
